import * as Sentry from '@sentry/react'
import env from './config/env'
import { getAppVersion } from '@/lib/helpers/getAppVersion.ts'

// Get environment from SENTRY_ENVIRONMENT first, then NODE_ENV, then default to development
const isProduction = env.isProduction()

const isDev = env.isDevelopment()
const environment = env.environment
const version = getAppVersion()

if (!isDev) {
  console.log(`[Sentry] Initializing in ${environment} mode`)

  Sentry.init({
    // Your Sentry project DSN - unique identifier for your project
    dsn: 'https://633eec8a091736bcfdfc217d351dab5f@o4509677792264192.ingest.de.sentry.io/4509677989134417',
    environment: environment,

    // Integrations - additional functionality modules
    integrations: [
      // Tracks page loads, navigation, and user interactions for performance monitoring
      Sentry.browserTracingIntegration({}),

      // Records user sessions for debugging - can be resource intensive3
      Sentry.replayIntegration({
        networkDetailAllowUrls: [window.location.origin],
        // Mask sensitive data in recordings
        // What: Replaces ALL text content with *** in recordings
        // Why: Privacy protection - prevents sensitive data (names, emails, addresses) from being recorded
        // Example: "John Doe" becomes "*** ***" in replay
        maskAllText: false,

        // What: Replaces ALL text content with *** in recordings
        // Why: Privacy protection - prevents sensitive data (names, emails, addresses) from being recorded
        // Example: "John Doe" becomes "*** ***" in replay
        maskAllInputs: false,

        // What: Masks all form inputs (text fields, passwords, etc.)
        // Why: Prevents capturing user passwords, credit cards, personal info
        // Example: Password field shows *** instead of actual password
        blockAllMedia: false,

        // What: Blocks images, videos, audio from being recorded
        // Why: Reduces bandwidth usage and storage costs significantly
        // Impact: Replays show placeholder boxes instead of actual media
      }),

      // Captures unhandled promise rejections and console errors
      Sentry.browserProfilingIntegration(),
    ],

    // PERFORMANCE MONITORING
    // Percentage of transactions to track (0.0 to 1.0)
    // 1.0 = 100% - VERY EXPENSIVE, use 0.1-0.3 for production
    //   Recommended Values:
    // Development: 0.05 - 0.1 (5-10%)
    // Production: 0.1 - 0.3 (10-30%)
    // High-traffic sites: 0.01 - 0.05 (1-5%)zx
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    // Controls what percentage of your app's performance transactions get tracked and sent to Sentry.

    // 🍆 tracesSampleRate: 0.2 (Production)
    // What: Track 20% of all transactions in production
    // Why: Balance between getting useful data and avoiding performance impact
    // Impact: Out of 100 page loads, only 20 will be tracked

    // 🍆 tracesSampleRate: 0.1 (Development)
    // What: Track 10% of all transactions in development
    // Why: Even lower rate for development to reduce noise and console spam
    // Impact: Out of 100 page loads, only 10 will be tracked

    // What are "Transactions"?
    // Page loads (user visits a page)
    // Navigation (user clicks a link, changes routes)
    // API calls (fetch requests to your backend)
    // User interactions (button clicks, form submissions)
    // 🍆 Why Not 100% (1.0)?
    //  Performance Impact:
    // CPU overhead: Tracking every single interaction
    // Memory usage: Storing performance data for every action
    // Network traffic: Sending massive amounts of data to Sentry
    // Sentry quota: You'll hit your monthly quota very quickly
    // User experience: Can slow down your app noticeably

    // 🍆 With 0.2 (20%):
    // 1000 users visit your site
    // 200 performance traces are recorded
    // You get good statistical data without overwhelming your system

    // Which domains to trace across (for distributed tracing)
    tracePropagationTargets: [
      'localhost', // ✅ Development (all localhost ports)
      /^https?:\/\/localhost/, // ✅ Explicit localhost with any port
      'self', // ✅ Same origin as your frontend
      'https://staging.use.buildlog.app',
      'https://use.buildlog.app',
      'https://demo.use.buildlog.app',
      'https://prologis.buildlog.app',
    ],

    // Same domain: https://yourapp.com/api/...
    // Subdomain: https://api.yourapp.com/...
    //   Different port: http://localhost:8000/api/... (development)
    // External service: https://your-backend.herokuapp.com/...

    // SESSION REPLAY SETTINGS
    // 🟢 replaysSessionSampleRate - Normal Sessions
    replaysSessionSampleRate: isProduction ? 0.01 : 1.0,
    // Records random user sessions regardless of whether errors occur
    // Percentage of normal sessions to record (0.0 to 1.0)
    // Keep low to avoid performance impact and storage costs
    // Small to Medium Apps:
    // < 10,000 monthly users
    // < 100 errors per day
    // Development/staging environments
    // Critical business applications where every error matters
    // Benefits:
    // Complete debugging context for every error
    // Easier to reproduce and fix bugs
    // Better user experience insights
    // ❌ When 1.0 (100%) is NOT Recommended:
    // Large Scale Apps:
    // > 50,000 monthly users
    // > 500 errors per day
    // High error rate applications

    // 🔴 replaysOnErrorSampleRate - Error Sessions
    // Development: 1.0 (100%) - Record all error sessions for debugging
    // replaysOnErrorSampleRate: isProduction ? 0.3 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // RELEASE AND ENVIRONMENT
    // Version identifier for tracking deployments
    release: version,

    // PRIVACY AND DATA COLLECTION
    // Include user IP, user agent, etc. - set false for privacy compliance
    sendDefaultPii: false,

    // PERFORMANCE OPTIMIZATIONS
    // Maximum number of breadcrumbs to keep (default: 100)
    //Breadcrumbs are a trail of events that happened before an error occurred. Think of them as a "timeline" leading up to the problem.
    maxBreadcrumbs: 50,

    // Automatically trim whitespace from event data
    //What This Does:
    // Controls how deeply nested objects are serialized when sending error data to Sentry. It prevents infinite loops and reduces payload size.
    // Why: Prevent huge payloads and circular reference issues
    // Default: Usually 5-6 levels (more data, bigger payloads)
    normalizeDepth: 3,

    // FILTERING AND SAMPLING
    // Filter out noisy errors
    // 🚫 Error Filtering -
    beforeSend(event, hint) {
      const ua = navigator.userAgent || ''
      if (/Android/i.test(ua)) {
        event.tags = {
          ...event.tags,
          platform: 'android',
          android_version: ua.match(/Android\s([0-9\.]+)/)?.[1] || 'unknown',
        }
      }
      // Don't send events for known non-critical errors
      if (event.exception) {
        const error = hint.originalException as Error | undefined
        if (error && error.name === 'ChunkLoadError') {
          return null // Don't send chunk load errors
        }
        if (error && error.message?.includes('ResizeObserver loop limit exceeded')) {
          return null // Don't send ResizeObserver errors
        }
      }

      // Don't send events in development unless they're errors
      // if (process.env.NODE_ENV === 'development' && event.level !== 'error') {
      //   return null
      // }

      return event
    },

    // Filter transactions to reduce noise
    beforeSendTransaction(event) {
      // Don't send very short transactions (likely not meaningful)
      if (event.spans && event.spans.length === 0) {
        return null
      }

      // Sample down navigation transactions in development
      // Math.random() > 0.3  // Exactly 30% pass through
      if (import.meta.env.MODE === 'development' && Math.random() > 0.3) {
        return null
      }

      return event
    },

    // Enable debug mode only in development
    // debug: process.env.NODE_ENV === 'development',
    debug: false, // ✅ This disables all logging
  })
} else {
  console.log(`skipping sentry init - dev mode detected`)
}
